import { Alert } from '@marrafa-gioale/gymx-ui-web'
import { createFileRoute } from '@tanstack/react-router'

import { useCurrentPermissions } from '@/api/queries'
import { PageHeader } from '@/components/layout/app-shell'
import { EquipmentPanel } from '@/features/organization/equipment-panel'
import { LocationsPanel } from '@/features/organization/locations-panel'
import { OrganizationCard } from '@/features/organization/organization-card'
import { AppFeaturesPanel } from '@/features/organization/app-features-panel'

/**
 * Anagrafica dell'organizzazione e sedi — contract `1.0.0-draft.27`.
 *
 * ## Perché è una pagina e non una sezione di «Impostazioni»
 *
 * «Impostazioni» risponde a «come si comporta questo pannello per me»: sessione,
 * tema, stato del backend — cose del browser che ho davanti. Qui invece si
 * scrivono i dati con cui si **emette una fattura a nome di qualcuno**, e le sedi
 * a cui il varco applica una capienza. Sono dati dell'azienda, non preferenze di
 * chi guarda, e metterli fra il tema chiaro e quello scuro li farebbe sembrare
 * altrettanto reversibili.
 *
 * ## I due permessi
 *
 * L'inventario delle attrezzature sta qui e non nella pagina «Schede»: un
 * attrezzo è dello **spazio fisico**, come una sala, e il permesso lo dichiara —
 * `facility:write`, che nei ruoli predefiniti ha il banco e non l'allenatore.
 *
 * `organization:read` per entrare, `organization:update` per scrivere
 * l'anagrafica — che nei ruoli predefiniti ha il solo titolare. Le sedi hanno i
 * propri: `location:read` ce l'ha ogni ruolo (una sede è l'indirizzo a cui si
 * lavora, non un dato riservato) e `location:write` no. Sono separati perché
 * separati sono i mestieri: chi apre una sede nuova non è per forza chi firma le
 * fatture.
 */
export const Route = createFileRoute('/_app/organizzazione/')({
  component: OrganizationPage,
})

function OrganizationPage() {
  const permissions = useCurrentPermissions()
  const canRead = permissions.has('organization:read')
  const canWriteOrganization = permissions.has('organization:update')
  const canWriteLocations = permissions.has('location:write')

  return (
    <>
      <PageHeader
        title="Organizzazione"
        description="Anagrafica fiscale e sedi. Da qui la partita IVA del cedente è registrabile senza passare dall’API."
      />

      <div className="space-y-4">
        {!canRead ? (
          // Non è una difesa — quella è lato server — ma la differenza fra un
          // pannello che dice perché una pagina è vuota e uno che sembra rotto.
          <Alert variant="neutral" title="Serve «organization:read»" icon={false}>
            Il tuo ruolo non legge l’anagrafica dell’organizzazione. Le sedi restano visibili se hai{' '}
            <code className="font-mono">location:read</code>.
          </Alert>
        ) : (
          <>
            <OrganizationCard canWrite={canWriteOrganization} />
            <AppFeaturesPanel canWrite={canWriteOrganization} />
          </>
        )}

        <LocationsPanel canWrite={canWriteLocations} />

        {/* L'inventario sta con le sedi e non con le schede: un attrezzo è
            dello **spazio**, e chi lo aggiunge è chi apre una sala — il
            permesso lo dice (`facility:write`, non `card:write_any`). */}
        <EquipmentPanel
          canWrite={permissions.has('facility:write')}
          canRead={permissions.has('facility:read')}
        />
      </div>
    </>
  )
}
